import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';

const API_URL = import.meta.env.VITE_API_URL;
const fmt = (n) => Number(n || 0).toLocaleString('fr-TN', { minimumFractionDigits: 0, maximumFractionDigits: 3 });

const PackAnalyticsView = () => {
    const [loading, setLoading] = useState(true);
    const [kpis, setKpis] = useState(null);
    const [rentabiliteParPack, setRentabiliteParPack] = useState([]);
    const [analyseProduits, setAnalyseProduits] = useState([]);
    const [installationsDansLeTemps, setInstallationsDansLeTemps] = useState([]);

    // Filtres
    const [periodPreset, setPeriodPreset] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    const getAuthHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem('token')}`
    });

    const handlePresetChange = (preset) => {
        setPeriodPreset(preset);
        const now = new Date();
        if (preset === 'week') {
            const start = new Date(now.setDate(now.getDate() - 7));
            setStartDate(start.toISOString().slice(0, 10));
            setEndDate(new Date().toISOString().slice(0, 10));
        } else if (preset === 'month') {
            const start = new Date(now.getFullYear(), now.getMonth(), 1);
            setStartDate(start.toISOString().slice(0, 10));
            setEndDate(new Date().toISOString().slice(0, 10));
        } else if (preset === '3months') {
            const start = new Date(now.getFullYear(), now.getMonth() - 3, 1);
            setStartDate(start.toISOString().slice(0, 10));
            setEndDate(new Date().toISOString().slice(0, 10));
        } else if (preset === 'year') {
            const start = new Date(now.getFullYear(), 0, 1);
            setStartDate(start.toISOString().slice(0, 10));
            setEndDate(new Date().toISOString().slice(0, 10));
        } else if (preset === 'all') {
            setStartDate('');
            setEndDate('');
        }
    };

    const fetchAnalytics = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/packs/analytics`, {
                headers: getAuthHeaders(),
                params: { startDate, endDate }
            });
            setKpis(res.data.kpis || null);
            setRentabiliteParPack(res.data.rentabiliteParPack || []);
            setAnalyseProduits(res.data.analyseProduits || []);
            setInstallationsDansLeTemps(res.data.installationsDansLeTemps || []);
        } catch (err) {
            console.error('Erreur fetchAnalytics:', err);
            Swal.fire('Erreur', 'Impossible de charger l\'analyse de rentabilité.', 'error');
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]);

    const filteredPacks = rentabiliteParPack.filter(p =>
        p.nomPack.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.puissance.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) {
        return <LoadingSpinner message="Calcul de la rentabilité réelle des packs..." />;
    }

    return (
        <div className="pack-analytics-container">
            {/* ── KPIs Bar ────────────────────────────────────────────────── */}
            <div className="analytics-kpi-grid">
                <div className="kpi-card blue">
                    <div className="kpi-icon">📦</div>
                    <div className="kpi-info">
                        <h3>{kpis?.totalPacks || 0}</h3>
                        <p>Total Packs Catalogue</p>
                    </div>
                </div>
                <div className="kpi-card purple">
                    <div className="kpi-icon">📋</div>
                    <div className="kpi-info">
                        <h3>{kpis?.totalAffectes || 0}</h3>
                        <p>Packs Engagés (Affectés)</p>
                    </div>
                </div>
                <div className="kpi-card teal">
                    <div className="kpi-icon">🚚</div>
                    <div className="kpi-info">
                        <h3>{kpis?.totalLivres || 0}</h3>
                        <p>Packs Livrés (BL Client)</p>
                    </div>
                </div>
                <div className="kpi-card green">
                    <div className="kpi-icon">⚡</div>
                    <div className="kpi-info">
                        <h3>{kpis?.totalInstalles || 0}</h3>
                        <p>Packs Installés Confirmés</p>
                    </div>
                </div>
                <div className="kpi-card orange">
                    <div className="kpi-icon">⏳</div>
                    <div className="kpi-info">
                        <h3>{kpis?.totalEnAttente || 0}</h3>
                        <p>Installations en Attente</p>
                    </div>
                </div>
            </div>

            {/* Bannières des taux de conversion de workflow */}
            <div className="analytics-conversion-bar mb-4 flex flex-wrap gap-4 text-xs font-medium text-slate-600 bg-slate-50 p-3 rounded-lg border border-slate-200">
                <span>🔄 <strong>Affecté → Livré :</strong> {kpis?.totalAffectes ? ((kpis.totalLivres / kpis.totalAffectes) * 100).toFixed(1) : 0}%</span>
                <span>•</span>
                <span>✅ <strong>Livré → Installé :</strong> {kpis?.totalLivres ? ((kpis.totalInstalles / kpis.totalLivres) * 100).toFixed(1) : 0}%</span>
            </div>

            <div className="analytics-financial-grid">
                <div className="fin-card">
                    <span className="fin-label">Chiffre d'Affaires Réel (Post-Remises)</span>
                    <span className="fin-val text-blue">{fmt(kpis?.caGenereReel)} DT</span>
                </div>
                <div className="fin-card">
                    <span className="fin-label">Coût d'Achat Total des Composants*</span>
                    <span className="fin-val text-purple">{fmt(kpis?.coutAchatTotal)} DT</span>
                </div>
                <div className="fin-card">
                    <span className="fin-label">Marge Brute Totale</span>
                    <span className="fin-val text-green">{fmt(kpis?.margeBruteTotale)} DT</span>
                </div>
                <div className="fin-card">
                    <span className="fin-label">Taux de Marge Moyenne</span>
                    <span className="fin-val text-orange">{fmt(kpis?.margeMoyennePourcentage)} %</span>
                </div>
            </div>
            <p className="text-xs text-slate-400 italic mb-4">
                * Note : Le coût d'achat est calculé sur la base des prix d'achat actuels du catalogue produits. Les prix de vente intègrent les prix réels des dossiers et devis (post-remises).
            </p>

            {/* ── Filtres de Période & Recherche ─────────────────────────── */}
            <div className="analytics-filter-bar">
                <div className="preset-buttons">
                    <button className={periodPreset === 'all' ? 'active' : ''} onClick={() => handlePresetChange('all')}>Toutes dates</button>
                    <button className={periodPreset === 'week' ? 'active' : ''} onClick={() => handlePresetChange('week')}>Cette semaine</button>
                    <button className={periodPreset === 'month' ? 'active' : ''} onClick={() => handlePresetChange('month')}>Ce mois</button>
                    <button className={periodPreset === '3months' ? 'active' : ''} onClick={() => handlePresetChange('3months')}>3 derniers mois</button>
                    <button className={periodPreset === 'year' ? 'active' : ''} onClick={() => handlePresetChange('year')}>Cette année</button>
                </div>
                <div className="date-inputs">
                    <input type="date" value={startDate} onChange={e => { setPeriodPreset('custom'); setStartDate(e.target.value); }} />
                    <span>à</span>
                    <input type="date" value={endDate} onChange={e => { setPeriodPreset('custom'); setEndDate(e.target.value); }} />
                </div>
                <div className="search-box">
                    <input
                        type="text"
                        placeholder="🔍 Filtrer pack..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>

            {/* ── Tableau de Rentabilité Réelle par Pack ──────────────────── */}
            <div className="analytics-section-card">
                <div className="section-header">
                    <h2>📊 Rentabilité Réelle par Pack (intègre les remises client)</h2>
                </div>
                <div className="table-responsive">
                    <table className="analytics-table">
                        <thead>
                            <tr>
                                <th>PACK</th>
                                <th>PUISSANCE</th>
                                <th>NB INSTALLÉS</th>
                                <th>CA RÉEL (DT)</th>
                                <th>COÛT ACHAT RÉEL (DT)</th>
                                <th>GAIN RÉEL (DT)</th>
                                <th>MARGE %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPacks.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="text-center py-4">Aucune donnée de rentabilité trouvée.</td>
                                </tr>
                            ) : (
                                filteredPacks.map(p => (
                                    <tr key={p.packId}>
                                        <td><strong>{p.nomPack}</strong></td>
                                        <td><span className="badge-puissance">{p.puissance}</span></td>
                                        <td className="text-center"><span className="badge-count">{p.nombreInstalles}</span></td>
                                        <td className="text-right">{fmt(p.caReel)} DT</td>
                                        <td className="text-right">{fmt(p.coutAchatReel)} DT</td>
                                        <td className="text-right font-bold text-green">{fmt(p.gainReel)} DT</td>
                                        <td className="text-right">
                                            <span className={`badge-marge ${p.margePourcentage >= 25 ? 'high' : p.margePourcentage >= 15 ? 'med' : 'low'}`}>
                                                {fmt(p.margePourcentage)} %
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── Graphiques Synthétiques ─────────────────────────────────── */}
            <div className="analytics-charts-grid">
                <div className="chart-card">
                    <h3>📈 Installations & CA dans le Temps (Mensuel)</h3>
                    {installationsDansLeTemps.length === 0 ? (
                        <p className="no-data-msg">Aucune installation enregistrée sur la période.</p>
                    ) : (
                        <div className="bar-chart-container">
                            {installationsDansLeTemps.map(item => (
                                <div key={item.mois} className="bar-item">
                                    <div className="bar-wrapper">
                                        <div
                                            className="bar-fill"
                                            style={{ height: `${Math.min(100, item.nombreInstallations * 20)}%` }}
                                            title={`${item.nombreInstallations} installation(s) — ${fmt(item.caMois)} DT`}
                                        ></div>
                                    </div>
                                    <span className="bar-label">{item.mois}</span>
                                    <small className="bar-count">{item.nombreInstallations} inst.</small>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="chart-card">
                    <h3>💡 Performance des Marges par Pack</h3>
                    <div className="margin-bars-list">
                        {filteredPacks.map(p => (
                            <div key={p.packId} className="margin-bar-item">
                                <div className="margin-bar-header">
                                    <span>{p.nomPack} ({p.puissance})</span>
                                    <strong>{fmt(p.margePourcentage)} %</strong>
                                </div>
                                <div className="margin-track">
                                    <div
                                        className={`margin-progress ${p.margePourcentage >= 25 ? 'green' : p.margePourcentage >= 15 ? 'orange' : 'red'}`}
                                        style={{ width: `${Math.min(100, Math.max(0, p.margePourcentage))}%` }}
                                    ></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── Tableau Consommation Réelle Produits vs Achats ───────────── */}
            <div className="analytics-section-card">
                <div className="section-header">
                    <h2>🔍 Consommation Réelle des Produits & Audit Stock</h2>
                </div>
                <div className="table-responsive">
                    <table className="analytics-table">
                        <thead>
                            <tr>
                                <th>PRODUIT</th>
                                <th>RÉFÉRENCE</th>
                                <th>ACHAT FOURNISSEUR</th>
                                <th>LIVRÉ CLIENT</th>
                                <th>INSTALLÉ CLIENT</th>
                                <th>STOCK THÉORIQUE</th>
                                <th>STOCK RÉEL SYSTÈME</th>
                                <th>ÉCART DÉTECTÉ</th>
                            </tr>
                        </thead>
                        <tbody>
                            {analyseProduits.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="text-center py-4">Aucun produit analysé.</td>
                                </tr>
                            ) : (
                                analyseProduits.map(prod => (
                                    <tr key={prod.produitId} className={prod.ecart !== 0 ? 'row-ecart-warning' : ''}>
                                        <td><strong>{prod.nomProduit}</strong></td>
                                        <td>{prod.reference}</td>
                                        <td className="text-center">{prod.quantiteAchetee}</td>
                                        <td className="text-center">{prod.quantiteLivree || 0}</td>
                                        <td className="text-center">{prod.quantiteInstallee}</td>
                                        <td className="text-center">{prod.stockTheorique}</td>
                                        <td className="text-center"><strong>{prod.stockReel}</strong></td>
                                        <td className="text-center">
                                            <span className={`badge-ecart ${prod.ecart === 0 ? 'zero' : prod.ecart > 0 ? 'positive' : 'negative'}`}>
                                                {prod.ecart > 0 ? `+${prod.ecart}` : prod.ecart}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default PackAnalyticsView;
